import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { Order, CartItem, TeamType } from '../../types'

interface SupabaseOrderStore {
  orders: Order[]
  loading: boolean
  error: string | null
  
  // Actions
  fetchOrders: () => Promise<void>
  createOrder: (items: CartItem[], orderInfo: {
    orderedBy: string
    team: TeamType
    trainerName: string
    deliveryMonth: 'August' | 'Dezember'
    notes?: string
  }) => Promise<{ error?: string }>
  updateOrderStatus: (id: string, status: Order['status'], approverInfo?: {
    approverName: string
    approvalComment?: string
  }) => Promise<{ error?: string }>
  deleteOrder: (id: string) => Promise<{ error?: string }>
  
  // Getters
  getOrdersByStatus: (status: Order['status']) => Order[]
  getPendingOrdersCount: () => number
}

export const useSupabaseOrderStore = create<SupabaseOrderStore>((set, get) => ({
  orders: [],
  loading: false,
  error: null,

  fetchOrders: async () => {
    set({ loading: true, error: null })
    try {
      const { profile } = useAuthStore.getState()
      if (!profile) throw new Error('Nicht angemeldet')

      let query = supabase.from('orders').select('*')
      
      // Trainer sehen nur ihre eigenen Bestellungen
      if (profile.role === 'trainer') {
        query = query.eq('user_id', profile.id)
      }
      
      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error

      // Transform snake_case to camelCase for UI compatibility
      const transformedOrders: Order[] = (data || []).map(order => ({
        id: order.id,
        items: order.items,
        status: order.status,
        previousStatus: (order.previous_status ?? undefined) as Order['previousStatus'],
        orderDate: order.order_date || order.created_at?.split('T')[0] || '',
        approvalDate: order.approval_date ?? undefined,
        approverName: order.approver_name ?? undefined,
        approvalComment: order.approval_comment ?? undefined,
        notes: order.notes ?? undefined,
        orderedBy: order.ordered_by,
        team: order.team as TeamType,
        trainerName: order.trainer_name,
        deliveryMonth: order.delivery_month,
      }))

      set({ orders: transformedOrders, loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  createOrder: async (items, orderInfo) => {
    try {
      const { user } = useAuthStore.getState()
      if (!user) return { error: 'Nicht angemeldet' }

      const { error } = await supabase
        .from('orders')
        .insert({
          items,
          ordered_by: orderInfo.orderedBy,
          team: orderInfo.team,
          trainer_name: orderInfo.trainerName,
          delivery_month: orderInfo.deliveryMonth,
          notes: orderInfo.notes,
          user_id: user.id,
        })
        .select()
        .single()

      if (error) throw error

      // Orders neu laden
      get().fetchOrders()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  updateOrderStatus: async (id, status, approverInfo) => {
    try {
      const updates: any = {
        status,
        previous_status: get().orders.find(o => o.id === id)?.status,
      }

      if (status === 'approved' && approverInfo) {
        updates.approval_date = new Date().toISOString().split('T')[0]
        updates.approver_name = approverInfo.approverName
        updates.approval_comment = approverInfo.approvalComment
      }

      const { error } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', id)

      if (error) throw error

      // Orders neu laden
      get().fetchOrders()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteOrder: async (id) => {
    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', id)

      if (error) throw error

      // Orders neu laden
      get().fetchOrders()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  getOrdersByStatus: (status) => {
    return get().orders.filter(order => order.status === status)
  },

  getPendingOrdersCount: () => {
    return get().orders.filter(order => order.status === 'pending').length
  },
}))
